// ── DB 관리 — 데이터 뷰 컴포넌트 ─────────────────────────────────────────────
// 테이블 행 목록 + 페이지네이션 + 행 편집 버튼
import React from 'react';
import {
  placeholderStyle, dataInfoBarStyle, tableWrapStyle, tblStyle, thStyle, trStyle, tdStyle,
  editRowBtnStyle, paginationStyle, pageBtnStyle,
  mobileDataRowCardStyle, mobileDataFieldStyle, mobileDataLabelStyle, mobileDataValueStyle,
} from './styles';
import { fmtNum, fmtCell } from './utils';

/**
 * 데이터 뷰: 테이블 행 목록 표시 (모바일: 카드, 데스크톱: 테이블)
 * @param {object[]} rows       — 현재 페이지 행 목록
 * @param {object[]} columns    — 컬럼 메타 배열 (column_name, is_sensitive 등)
 * @param {object}   pagination — { page, total, total_pages, limit }
 * @param {boolean}  loading    — 로딩 중 여부
 * @param {string}   pkColumn   — PK 컬럼명
 * @param {boolean}  isMobile   — 모바일 여부
 * @param {Function} onPageChange — 페이지 변경 콜백 (page) => void
 * @param {Function} onEditRow    — 행 편집 클릭 콜백 (row) => void
 */
export function DataView({ rows, columns, pagination, loading, pkColumn, isMobile, onPageChange, onEditRow }) {
  if (loading) return <div style={placeholderStyle}>데이터 로딩 중...</div>;
  if (!columns.length) return <div style={placeholderStyle}>데이터가 없습니다.</div>;

  return (
    <div>
      {/* 정보 바: 전체 건수 + 페이지 번호 */}
      <div style={dataInfoBarStyle}>
        <span>전체 {fmtNum(pagination.total)}건</span>
        <span style={{ color: 'var(--text-muted)' }}>·</span>
        <span>{pagination.page} / {pagination.total_pages} 페이지</span>
        {!isMobile && (
          <>
            <span style={{ color: 'var(--text-muted)' }}>·</span>
            <span style={{ color: '#ef4444', fontSize: 11 }}>🔒 민감 컬럼은 자동 마스킹 및 수정 불가</span>
          </>
        )}
      </div>

      {isMobile ? (
        /* 모바일: 행 카드 목록 */
        <div style={{ display: 'grid', gap: 8, padding: '0 12px 12px' }}>
          {rows.map((row, i) => (
            <div key={i} style={mobileDataRowCardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                {/* PK 표시 */}
                <span style={{ fontSize: 11, color: '#7c3aed', fontFamily: 'monospace', fontWeight: 700 }}>
                  {pkColumn}: {String(row[pkColumn] ?? '-')}
                </span>
                <button style={editRowBtnStyle} onClick={() => onEditRow(row)} title="이 행 수정">✏️ 수정</button>
              </div>
              {/* 나머지 컬럼: 이름=값 목록 */}
              <div style={{ display: 'grid', gap: 3 }}>
                {columns.filter(c => c.column_name !== pkColumn).map(col => (
                  <div key={col.column_name} style={mobileDataFieldStyle}>
                    <span style={mobileDataLabelStyle}>
                      {col.column_name}{col.is_sensitive ? ' 🔒' : ''}
                    </span>
                    <span style={mobileDataValueStyle}>
                      {fmtCell(row[col.column_name])}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* 데스크톱: 테이블 */
        <div style={tableWrapStyle}>
          <table style={tblStyle}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 40 }}></th>
                {columns.map(col => (
                  <th key={col.column_name} style={thStyle}>
                    <span>{col.column_name}</span>
                    {col.is_sensitive && <span style={{ marginLeft: 4, fontSize: 10 }}>🔒</span>}
                    {col.column_name === pkColumn && <span style={{ marginLeft: 4, fontSize: 10, color: '#7c3aed' }}>PK</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} style={trStyle(i)}>
                  <td style={{ ...tdStyle, padding: '4px 8px' }}>
                    <button style={editRowBtnStyle} onClick={() => onEditRow(row)} title="이 행 수정">✏️</button>
                  </td>
                  {columns.map(col => (
                    <td key={col.column_name} style={{ ...tdStyle, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {fmtCell(row[col.column_name])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 페이지네이션 버튼 */}
      {pagination.total_pages > 1 && (
        <div style={paginationStyle}>
          <button style={pageBtnStyle(false)} disabled={pagination.page <= 1} onClick={() => onPageChange(1)}>«</button>
          <button style={pageBtnStyle(false)} disabled={pagination.page <= 1} onClick={() => onPageChange(pagination.page - 1)}>‹</button>
          {Array.from({ length: Math.min(7, pagination.total_pages) }, (_, i) => {
            const half = 3;
            let start = Math.max(1, pagination.page - half);
            const end  = Math.min(pagination.total_pages, start + 6);
            start = Math.max(1, end - 6);
            const p = start + i;
            if (p > pagination.total_pages) return null;
            return (
              <button key={p} style={pageBtnStyle(p === pagination.page)} onClick={() => onPageChange(p)}>{p}</button>
            );
          })}
          <button style={pageBtnStyle(false)} disabled={pagination.page >= pagination.total_pages} onClick={() => onPageChange(pagination.page + 1)}>›</button>
          <button style={pageBtnStyle(false)} disabled={pagination.page >= pagination.total_pages} onClick={() => onPageChange(pagination.total_pages)}>»</button>
        </div>
      )}
    </div>
  );
}
